import { useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { SourceRowDto } from '@lib/dto/source-numbers.dto'
import { dollarsFromCents } from '@lib/helpers/format'
import { Table, TableBody, TableCell, TableEmptyRow, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatCount, formatMicros } from '@/lib/adFormat'
import { cn } from '@/lib/utils'

/** Booked value per dollar spent: `$4.20 per $1`. */
export function formatReturn(ratio: number): string {
  return `$${ratio.toFixed(2)} per $1`
}

const MICROS_PER_CENT = 10_000

/** Google Ads' campaigns and its couples with no campaign, as one row: the money worked out from the totals, never averaged. */
function googleTotal(rows: readonly SourceRowDto[]): SourceRowDto {
  const sum = (pick: (r: SourceRowDto) => number | null): number | null => {
    const values = rows.map(pick).filter((v): v is number => v !== null)
    return values.length === 0 ? null : values.reduce((a, b) => a + b, 0)
  }
  const inquiries = rows.reduce((a, r) => a + r.inquiries, 0)
  const bookings = rows.reduce((a, r) => a + r.bookings, 0)
  const cost = sum((r) => r.costMicros)
  const value = sum((r) => r.bookedValueCents)
  const currencyCode = rows.find((r) => r.currencyCode !== null)?.currencyCode ?? null
  return {
    channel: 'google_ads',
    listingId: null,
    campaignId: null,
    label: 'Google Ads',
    inquiries,
    tours: sum((r) => r.tours),
    bookings,
    bookingsWithoutValue: rows.reduce((a, r) => a + r.bookingsWithoutValue, 0),
    bookedValueCents: value,
    costMicros: cost,
    currencyCode,
    costPerInquiryMicros: cost === null || inquiries === 0 ? null : Math.round(cost / inquiries),
    costPerBookingMicros: cost === null || bookings === 0 ? null : Math.round(cost / bookings),
    returnRatio: cost === null || cost <= 0 || value === null || currencyCode !== 'USD' ? null : (value * MICROS_PER_CENT) / cost,
  }
}

interface SourceTableProps {
  rows: SourceRowDto[]
  /** Whether money is shown: booked value, cost and return. Without it those columns aren't there. */
  showsMoney: boolean
  heading?: string
  /** The table's own controls, in its header band. */
  actions?: ReactNode
  /** Google Ads as one row opening to its campaigns (the default), or its campaigns as rows of their own. */
  groupGoogle?: boolean
  /** Adds what each inquiry cost, beside the inquiries, where money is shown. */
  showsCostPerInquiry?: boolean
  /**
   * Marks a source that cost something and booked no one. The Pipeline's weeks
   * have had time to book; an Ads page's newest two weeks mostly haven't, so
   * it leaves the mark off.
   */
  marksUnbooked?: boolean
}

function money(micros: number | null, currencyCode: string | null): string {
  return micros === null || currencyCode === null ? '—' : formatMicros(micros, currencyCode)
}

/** A source that cost something and booked no one: the row's cost per booking says so, in the attention color. */
function CostPerBooking({ row, marksUnbooked }: { row: SourceRowDto; marksUnbooked: boolean }) {
  const number = 'text-right font-mono tabular-nums'
  if (marksUnbooked && row.bookings === 0 && (row.costMicros ?? 0) > 0) {
    return <TableCell className={cn(number, 'bg-hold-soft font-sans font-medium text-hold-ink')}>None booked yet</TableCell>
  }
  return <TableCell className={number}>{money(row.costPerBookingMicros, row.currencyCode)}</TableCell>
}

function Row({
  row,
  showsMoney,
  showsTours,
  showsCostPerInquiry,
  marksUnbooked,
  nested = false,
  toggle,
}: {
  row: SourceRowDto
  showsMoney: boolean
  showsTours: boolean
  showsCostPerInquiry: boolean
  marksUnbooked: boolean
  nested?: boolean
  toggle?: { open: boolean; onToggle: () => void } | undefined
}) {
  const number = 'text-right font-mono tabular-nums'
  return (
    <TableRow>
      <TableCell className={cn('min-w-48', nested ? 'pl-10 text-control text-ink' : 'font-medium text-ink')}>
        {toggle === undefined ? (
          row.label
        ) : (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-left font-medium text-ink"
            aria-expanded={toggle.open}
            onClick={toggle.onToggle}
          >
            {toggle.open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {row.label}
          </button>
        )}
      </TableCell>
      <TableCell className={number}>{formatCount(row.inquiries)}</TableCell>
      {showsMoney && showsCostPerInquiry ? <TableCell className={number}>{money(row.costPerInquiryMicros, row.currencyCode)}</TableCell> : null}
      {showsTours ? <TableCell className={number}>{row.tours === null ? '—' : formatCount(row.tours)}</TableCell> : null}
      <TableCell className={number}>
        {formatCount(row.bookings)}
        {showsMoney && row.bookingsWithoutValue > 0 ? (
          <span className="block font-sans text-xs text-ink-muted">{formatCount(row.bookingsWithoutValue)} without a value</span>
        ) : null}
      </TableCell>
      {showsMoney ? (
        <>
          <TableCell className={number}>{row.bookedValueCents === null ? '—' : dollarsFromCents(row.bookedValueCents)}</TableCell>
          <TableCell className={number}>{money(row.costMicros, row.currencyCode)}</TableCell>
          <CostPerBooking row={row} marksUnbooked={marksUnbooked} />
          <TableCell className={number}>{row.returnRatio === null ? '—' : formatReturn(row.returnRatio)}</TableCell>
        </>
      ) : null}
    </TableRow>
  )
}

/** Google Ads with a single row: its campaign's, named as Google Ads', or its couples with no campaign known. */
function soleGoogleRow(row: SourceRowDto): SourceRowDto {
  return { ...row, label: row.campaignId === null ? 'Google Ads' : `Google Ads: ${row.label}` }
}

/** A campaign's row says its name; the couples Google Ads brought with no campaign known say so. */
function campaignRow(row: SourceRowDto): SourceRowDto {
  return row.campaignId === null ? { ...row, label: 'No campaign known' } : row
}

/**
 * Where a period's couples came from (docs/ad-attribution.spec.md): one row per
 * channel, each listing site its own row, Google Ads opening to its campaigns.
 */
export function SourceTable({
  rows,
  showsMoney,
  heading = 'Where couples came from',
  actions,
  groupGoogle = true,
  showsCostPerInquiry = false,
  marksUnbooked = false,
}: SourceTableProps) {
  const [googleOpen, setGoogleOpen] = useState(false)
  const showsTours = rows.some((r) => r.tours !== null)
  const google = groupGoogle ? rows.filter((r) => r.channel === 'google_ads') : []
  const rest = groupGoogle ? rows.filter((r) => r.channel !== 'google_ads') : rows.map(campaignRow)
  const columns = 3 + (showsTours ? 1 : 0) + (showsMoney ? 4 : 0) + (showsMoney && showsCostPerInquiry ? 1 : 0)
  const props = { showsMoney, showsTours, showsCostPerInquiry, marksUnbooked }
  return (
    <Table heading={heading} actions={actions}>
      <TableHeader>
        <TableHead>{groupGoogle ? 'Source' : 'Campaign'}</TableHead>
        <TableHead className="text-right">Inquiries</TableHead>
        {showsMoney && showsCostPerInquiry ? <TableHead className="text-right">Cost per inquiry</TableHead> : null}
        {showsTours ? <TableHead className="text-right">Tours</TableHead> : null}
        <TableHead className="text-right">Bookings</TableHead>
        {showsMoney ? (
          <>
            <TableHead className="text-right">Booked value</TableHead>
            <TableHead className="text-right">Cost</TableHead>
            <TableHead className="text-right">Cost per booking</TableHead>
            <TableHead className="text-right">Return</TableHead>
          </>
        ) : null}
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? <TableEmptyRow colSpan={columns}>No couples in this period.</TableEmptyRow> : null}
        {google.length === 1 && google[0] !== undefined ? <Row row={soleGoogleRow(google[0])} {...props} /> : null}
        {google.length > 1 ? (
          <>
            <Row row={googleTotal(google)} {...props} toggle={{ open: googleOpen, onToggle: () => setGoogleOpen((o) => !o) }} />
            {googleOpen ? google.map((row) => <Row key={row.campaignId ?? 'none'} row={campaignRow(row)} {...props} nested />) : null}
          </>
        ) : null}
        {rest.map((row) => (
          <Row key={`${row.channel}:${row.listingId ?? ''}:${row.campaignId ?? ''}`} row={row} {...props} />
        ))}
      </TableBody>
    </Table>
  )
}
